import {} from 'react-icons/';
import './widget.css';

import { Rating } from '@mui/material';
import { FieldConfig, useField } from 'formik';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { MdStar } from 'react-icons/md';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { useKeyboardIsShowing } from '../../../../../theme/keyboard.hook';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { WidgetLabel } from './widget-label';
import {
  RatingWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface RatingInputProps extends RatingWidgetProps {}

function RatingInput(props: RatingInputProps & FieldConfig): ReactElement {
  const {
    value,
    label,
    description,
    showDescription,
    disabled,
    ionItemProps,
    extensions,
    isNested,
    transparentBackground,
    ...otherProps
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);
  const inputRef = useRef<HTMLIonSelectElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);
  const isKeyboardShowing = useKeyboardIsShowing();

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    transparentBackground,
  });

  return (
    <IonItem
      className={classList}
      lines="full"
      disabled={isDisabled}
      onTouchStart={() => {
        if (isKeyboardShowing) {
          const el = document.activeElement as HTMLInputElement;
          if (typeof el?.blur === 'function') {
            el?.blur();
          }
        }
      }}
      {...ionItemProps}
    >
      <WidgetLabel
        label={label}
        description={description}
        showDescription={showDescription}
        error={meta.error}
        forceDisable={isDisabled}
      />
      <Rating
        value={field.value}
        onChange={(_, value) => {
          helper.setValue(value);
        }}
        emptyIcon={<MdStar color="var(--ion-color-light)" />}
      />
      {rightElement}
    </IonItem>
  );
}

export { RatingInput };
